import { AxiosError } from 'axios';
import { toastError } from '@/shared/lib/utils/toast';
import { CancelledError } from '@tanstack/react-query';

/**
 * Типы данных ошибки API
 */
type ErrorData = {
  detail?: string | Array<{ msg?: string; message?: string }> | { message: string };
  message?: string;
};

/**
 * Извлекает сообщение об ошибке из различных форматов ответа API
 * @param errorData - Данные ошибки из ответа API
 * @returns Сообщение об ошибке
 */
export const extractErrorMessage = (errorData: unknown): string => {
  const defaultMessage = 'Произошла ошибка при выполнении запроса';

  if (!errorData || typeof errorData !== 'object') {
    return defaultMessage;
  }

  const data = errorData as ErrorData;

  // Формат 1: detail - строка
  if (typeof data.detail === 'string') {
    return data.detail;
  }

  // Формат 2: detail - массив объектов с msg или message
  if (Array.isArray(data.detail) && data.detail.length > 0) {
    const firstError = data.detail[0];
    if (firstError.msg) {
      return firstError.msg;
    }
    if (firstError.message) {
      return firstError.message;
    }
  }

  // Формат 3: detail - объект (не массив) с message
  if (data.detail && typeof data.detail === 'object' && !Array.isArray(data.detail) && 'message' in data.detail) {
    return data.detail.message;
  }

  // Формат 4: прямое поле message
  if (data.message) {
    return data.message;
  }

  return defaultMessage;
};

/**
 * Сообщение об ошибке запроса для показа рядом с местом, где она возникла.
 * Клиент API отклоняет запрос парой «данные ответа + статус», поэтому текст берётся
 * из данных ответа, а не из полей ошибки axios — их к этому моменту уже нет.
 */
export const getApiErrorMessage = (error: unknown): string => {
  const errorData = (error as { data?: unknown } | null | undefined)?.data;

  return extractErrorMessage(errorData ?? error);
};

/**
 * Сообщение об ошибке запроса, ответ которого читался файлом. Причина отказа приходит тем же
 * телом ответа, но неразобранной: `responseType: 'blob'` относится ко всему ответу, включая отказ.
 */
export const readApiErrorMessage = async (error: unknown): Promise<string> => {
  const data = (error as { data?: unknown } | null | undefined)?.data;
  if (!(data instanceof Blob)) return getApiErrorMessage(error);

  try {
    return extractErrorMessage(JSON.parse(await data.text()));
  } catch {
    // Отказ пришёл не разбираемым телом — причина остаётся общей
    return extractErrorMessage(null);
  }
};

/**
 * Код ответа, которым запрос отклонён. Берётся из пары «данные ответа + статус», которой
 * клиент API отклоняет запрос: полей ошибки axios к этому моменту уже нет.
 * `undefined` — ответа не было вовсе (сетевой сбой, обрыв соединения).
 */
export const getApiErrorStatus = (error: unknown): number | undefined =>
  (error as { status?: number } | null | undefined)?.status;

let unauthorizedHandler: (() => void) | null = null;

/**
 * Регистрирует обработчик ошибки 401 (Unauthorized)
 * Вызывается из слоя app для установки callback с доступом к auth store.
 * Возвращает функцию снятия обработчика
 */
export const registerUnauthorizedHandler = (handler: () => void): (() => void) => {
  unauthorizedHandler = handler;

  return () => {
    // Снимается только свой обработчик: к моменту снятия зарегистрированным
    // может оказаться уже следующий
    if (unauthorizedHandler === handler) {
      unauthorizedHandler = null;
    }
  };
};

/**
 * Обрабатывает ошибку 401 (Unauthorized)
 * Вызывает зарегистрированный обработчик для перенаправления на страницу авторизации
 */
export const handleUnauthorizedError = (): void => {
  unauthorizedHandler?.();
};

/**
 * Обрабатывает ошибки Axios
 * @param error - Ошибка Axios
 * @returns Отклоненный Promise с данными ошибки
 */
export const handleAxiosError = (error: AxiosError): Promise<{ data: ErrorData; status?: number }> => {
  const status = error.response?.status;
  const errorData = error.response?.data;

  // Handle unauthorized errors (401) - не показываем toast, так как происходит редирект
  if (status === 401) {
    handleUnauthorizedError();
    return Promise.reject({ data: errorData, status });
  }

  // Извлекаем и показываем сообщение об ошибке
  const errorMessage = extractErrorMessage(errorData);
  if (!error.config?.silentError && !(error instanceof CancelledError) && error.code !== 'ERR_CANCELED') {
    toastError('Error', {
      description: errorMessage,
    });
  }

  return Promise.reject({ data: errorData, status });
};

/**
 * Обрабатывает не-Axios ошибки (сетевые ошибки и другие типы)
 * @param error - Ошибка любого типа
 * @returns Отклоненный Promise
 */
export const handleUnknownError = (error: unknown): Promise<never> => {
  const errorMessage = error instanceof Error ? error.message : 'Произошла неизвестная ошибка';
  toastError(errorMessage);
  return Promise.reject(error);
};
